import { useMemo, useState, useEffect } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBox,
  faBoxes,
  faStore,
  faEye,
  faEdit,
  faArrowRight,
  faArrowLeft,
  faCalendarAlt,
  faSpinner,
  faChevronLeft,
  faChevronRight,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import StockDetails from "./StockDetails";
import { stockService } from "../../../services/service/stockService";

const formatCurrency = (n) =>
  new Intl.NumberFormat("sw-TZ", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n || 0)) + " TSH";

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  onItemsPerPageChange,
  itemsPerPageOptions,
}) => {
  if (totalItems === 0) return null;

  const pageNumbers = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  for (let i = start; i <= end; i++) pageNumbers.push(i);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-3 pt-3 border-t border-gray-200">
      <div className="flex items-center gap-3 text-sm text-gray-600 flex-wrap">
        <span>
          Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}{" "}
          to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
          records
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-gray-600">Rows per page:</span>
          <Select
            value={String(itemsPerPage)}
            onValueChange={(val) => onItemsPerPageChange(Number(val))}
          >
            <SelectTrigger className="h-7 w-16 px-2 text-sm border border-gray-300 rounded-md bg-white text-gray-600 focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {itemsPerPageOptions.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-8 px-2 border-gray-300 hover:border-blue-800"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
        </Button>
        {start > 1 && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(1)}
              className="h-8 w-8 p-0 border-gray-300 hover:border-blue-800 text-sm text-gray-600"
            >
              1
            </Button>
            {start > 2 && <span className="text-sm text-gray-600">…</span>}
          </>
        )}
        {pageNumbers.map((number) => (
          <Button
            key={number}
            variant={currentPage === number ? "default" : "outline"}
            size="sm"
            onClick={() => onPageChange(number)}
            className={`h-8 w-8 p-0 text-sm ${
              currentPage === number
                ? "bg-blue-900 hover:bg-blue-900 text-white"
                : "border-gray-300 hover:border-blue-800 hover:bg-blue-50 text-gray-600"
            }`}
          >
            {number}
          </Button>
        ))}
        {end < totalPages && (
          <>
            {end < totalPages - 1 && (
              <span className="text-sm text-gray-600">…</span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(totalPages)}
              className="h-8 w-8 p-0 border-gray-300 hover:border-blue-800 text-sm text-gray-600"
            >
              {totalPages}
            </Button>
          </>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-8 px-2 border-gray-300 hover:border-blue-800"
        >
          <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

const TableView = ({
  stocks,
  shops,
  shopName,
  onViewStock,
  onEditStock,
  canManageStock,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [stockFilter, setStockFilter] = useState("all");
  const itemsPerPageOptions = [5, 10, 15, 20, 50, 100];

  const allRows = useMemo(() => {
    const rows = [];
    stocks.forEach((stock, stockIdx) => {
      const movements = Array.isArray(stock.movements) ? stock.movements : [];
      movements.forEach((m) => {
        rows.push({
          ...m,
          stock,
          stockNo: stockIdx + 1,
          productName: stock.product?.product_name || "Product",
          shopName,
        });
      });
    });
    return rows;
  }, [stocks, shopName]);

  const filteredRows = useMemo(() => {
    if (stockFilter === "all") return allRows;
    const num = Number(stockFilter);
    return allRows.filter((r) => r.stockNo === num);
  }, [allRows, stockFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [stockFilter, itemsPerPage]);

  const totalItems = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * itemsPerPage;
  const pageRows = filteredRows.slice(startIndex, startIndex + itemsPerPage);

  const stockOptions = useMemo(
    () =>
      stocks.map((_, idx) => ({
        value: String(idx + 1),
        label: `Stock #${idx + 1}`,
      })),
    [stocks],
  );

  if (allRows.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-600">
        No movements to display
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm bg-white">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faFilter} className="h-3 w-3 text-gray-600" />
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
            Stock filter
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Select value={stockFilter} onValueChange={setStockFilter}>
            <SelectTrigger className="h-8 w-36 px-2 text-sm border border-gray-300 rounded-md bg-white text-gray-600 focus:ring-0 focus:ring-offset-0">
              <SelectValue placeholder="All stocks" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stocks</SelectItem>
              {stockOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {stockFilter !== "all" && (
            <button
              type="button"
              onClick={() => setStockFilter("all")}
              className="text-xs text-blue-700 hover:underline font-medium"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-gray-50 border-b-2 border-gray-200">
              <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                #
              </th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                Stock No
              </th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                Product
              </th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                Shop
              </th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                Type
              </th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                Date
              </th>
              <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                Qty
              </th>
              <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                Remaining
              </th>
              <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                Price
              </th>
              <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="py-8 text-center text-sm text-gray-600"
                >
                  No rows match the current filter
                </td>
              </tr>
            ) : (
              pageRows.map((row, i) => {
                const isIn = row.type === "IN";
                const isOut = row.type === "OUT";
                const isBalance = row.type === "BALANCE";
                const prevRow = pageRows[i - 1];
                const isNewGroup = !prevRow || prevRow.stockNo !== row.stockNo;

                return (
                  <tr
                    key={`${row.stockNo}-${row.id}`}
                    className={`transition-colors hover:bg-blue-50/40 ${
                      isNewGroup && i > 0
                        ? "border-t-2 border-dashed border-gray-200"
                        : "border-t border-gray-100"
                    } ${row.stockNo % 2 === 0 ? "bg-gray-50/40" : "bg-white"}`}
                  >
                    <td className="py-2.5 px-3 text-sm text-gray-600">
                      {startIndex + i + 1}
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-900 text-white text-xs font-semibold">
                        Stock #{row.stockNo}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-sm text-gray-600 max-w-[160px] truncate">
                      {row.productName}
                    </td>
                    <td className="py-2.5 px-3 text-sm text-gray-600 max-w-[160px] truncate">
                      {row.shopName}
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      <Badge
                        variant="outline"
                        className={`border text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 w-fit font-semibold ${
                          isIn
                            ? "bg-green-100 text-green-700 border-green-200"
                            : isOut
                              ? "bg-red-100 text-red-700 border-red-200"
                              : "bg-blue-100 text-blue-700 border-blue-200"
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={
                            isIn ? faArrowRight : isOut ? faArrowLeft : faBox
                          }
                          className="h-2.5 w-2.5"
                        />
                        {isIn ? "IN" : isOut ? "OUT" : "BAL"}
                      </Badge>
                    </td>
                    <td className="py-2.5 px-3 text-sm text-gray-600 whitespace-nowrap">
                      {formatDate(row.date)}
                    </td>
                    <td className="py-2.5 px-3 text-right text-sm font-semibold text-gray-600 whitespace-nowrap">
                      {isBalance ? (
                        <span className="text-gray-600">—</span>
                      ) : (
                        row.quantity
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right text-sm font-semibold text-blue-600 whitespace-nowrap">
                      {row.remaining}
                    </td>
                    <td className="py-2.5 px-3 text-right text-sm font-medium text-gray-600 whitespace-nowrap">
                      {row.price != null && row.price > 0
                        ? formatCurrency(row.price)
                        : "—"}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            const fullShop =
                              shops.find(
                                (s) => s.shopId === row.stock.shopId,
                              ) || null;
                            onViewStock(
                              row.stock,
                              fullShop || {
                                shopId: row.stock.shopId,
                                shop_name: shopName,
                              },
                            );
                          }}
                          className="h-7 w-7 rounded-md flex items-center justify-center text-blue-600 hover:bg-blue-100 transition-colors"
                          title="View full stock details"
                        >
                          <FontAwesomeIcon
                            icon={faEye}
                            className="h-3.5 w-3.5"
                          />
                        </button>
                        {canManageStock && (
                          <button
                            type="button"
                            onClick={() => onEditStock(row.stock)}
                            className="h-7 w-7 rounded-md flex items-center justify-center text-green-600 hover:bg-green-100 transition-colors"
                            title="Edit stock"
                          >
                            <FontAwesomeIcon
                              icon={faEdit}
                              className="h-3.5 w-3.5"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="px-3 pb-3">
        <Pagination
          currentPage={safeCurrentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
          itemsPerPageOptions={itemsPerPageOptions}
        />
      </div>
    </div>
  );
};

const ProductStockDetails = ({
  productGroup,
  shop,
  shops = [],
  onClose,
  canManageStock,
  onEditStock,
}) => {
  const [drillDown, setDrillDown] = useState(null);
  const [enrichedStocks, setEnrichedStocks] = useState([]);
  const [isFetchingSales, setIsFetchingSales] = useState(false);

  const baseStocks = useMemo(() => {
    if (!productGroup?.stocks?.length) return [];
    if (!shop?.shopId) return productGroup.stocks;
    return productGroup.stocks.filter((s) => s.shopId === shop.shopId);
  }, [productGroup, shop]);

  const selectedShop =
    shop || shops.find((s) => s.shopId === baseStocks[0]?.shopId) || null;

  const selectedShopName =
    selectedShop?.shop_name || selectedShop?.shopName || "Shop";

  useEffect(() => {
    let isCancelled = false;

    const fetchAll = async () => {
      if (!baseStocks.length) {
        setEnrichedStocks([]);
        return;
      }

      setIsFetchingSales(true);
      try {
        const results = await Promise.all(
          baseStocks.map(async (stock) => {
            const stockId = stock.stockId || stock.id;
            if (!stockId) return stock;

            if (
              Array.isArray(stock.movements) &&
              stock.movements.length > 0 &&
              stock.summary
            ) {
              return { ...stock, _detailsFetched: true };
            }

            try {
              const response = await stockService.getWithSales(stockId);
              const payload = response?.data?.data ?? response?.data ?? null;

              const movements = Array.isArray(payload?.movements)
                ? payload.movements
                : Array.isArray(stock.movements)
                  ? stock.movements
                  : [];

              const summary = payload?.summary || stock.summary || {};

              const stockPayload = payload?.stock || {};

              return {
                ...stock,
                ...stockPayload,
                movements,
                summary: { ...(stock.summary || {}), ...summary },
                _detailsFetched: true,
              };
            } catch (err) {
              console.error(
                `Failed to fetch details for stock ${stockId}:`,
                err,
              );
              return {
                ...stock,
                movements: stock.movements || [],
                _detailsFetched: true,
              };
            }
          }),
        );

        if (!isCancelled) setEnrichedStocks(results);
      } finally {
        if (!isCancelled) setIsFetchingSales(false);
      }
    };

    fetchAll();
    return () => {
      isCancelled = true;
    };
  }, [baseStocks]);

  const displayStocks = enrichedStocks.length ? enrichedStocks : baseStocks;

  const totals = useMemo(() => {
    let totalIn = 0;
    let totalOut = 0;
    let totalRemaining = 0;
    let totalPurchase = 0;
    let totalSelling = 0;

    displayStocks.forEach((s) => {
      totalIn += Number(s.summary?.totalIn ?? 0);
      totalOut += Number(s.summary?.totalOut ?? 0);
      totalRemaining += Number(s.summary?.totalRemaining ?? 0);
      totalPurchase += Number(s.purchase_price_total ?? 0);
      totalSelling += Number(s.selling_price_total ?? 0);
    });

    return {
      totalIn,
      totalOut,
      totalRemaining,
      totalPurchase,
      totalSelling,
      profit: totalSelling - totalPurchase,
    };
  }, [displayStocks]);

  const handleViewStock = (stock, shopObj) => {
    setDrillDown({ stock, shop: shopObj });
  };

  const handleEditStock = (stock) => {
    onClose();
    if (onEditStock) onEditStock(stock);
  };

  if (drillDown) {
    return (
      <StockDetails
        stock={drillDown.stock}
        shop={drillDown.shop}
        onClose={() => setDrillDown(null)}
        onEdit={
          canManageStock
            ? (stock) => {
                setDrillDown(null);
                onClose();
                if (onEditStock) onEditStock(stock);
              }
            : undefined
        }
      />
    );
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Stock list — ${selectedShopName}`}
      maxWidth="sm:max-w-7xl"
      footer={
        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-white rounded-md border border-blue-100">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shadow-sm shrink-0">
            <FontAwesomeIcon icon={faBoxes} className="text-blue-600 h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base sm:text-lg font-bold text-gray-600 truncate">
              {productGroup?.productName}
            </p>
            <p className="text-sm sm:text-sm text-gray-600">
              <FontAwesomeIcon icon={faStore} className="mr-1 h-3 w-3" />
              {selectedShopName} · {displayStocks.length}{" "}
              {displayStocks.length === 1 ? "stock" : "stocks"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Badge
              variant="outline"
              className="bg-blue-50 text-blue-700 border-blue-200 text-sm"
            >
              Purchase {formatCurrency(totals.totalPurchase)}
            </Badge>
            <Badge
              variant="outline"
              className="bg-green-50 text-green-700 border-green-200 text-sm"
            >
              Selling {formatCurrency(totals.totalSelling)}
            </Badge>
            <Badge
              variant="outline"
              className={`text-sm ${
                totals.profit >= 0
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-red-50 text-red-700 border-red-200"
              }`}
            >
              Expected profit {formatCurrency(totals.profit)}
            </Badge>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
              Stock movements
            </span>
            {isFetchingSales && (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 ml-2">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-3 w-3 animate-spin"
                />
                Loading sales...
              </span>
            )}
          </div>
          <span className="text-xs text-gray-600">
            Flat list of all stock movements
          </span>
        </div>

        <TableView
          stocks={displayStocks}
          shops={shops}
          shopName={selectedShopName}
          onViewStock={handleViewStock}
          onEditStock={handleEditStock}
          canManageStock={canManageStock}
        />

        <div className="bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
          <div className="flex flex-wrap items-center gap-4 font-semibold text-gray-600">
            <span>
              Total IN: <span className="text-green-600">{totals.totalIn}</span>
            </span>
            <span>
              Total OUT: <span className="text-red-600">{totals.totalOut}</span>
            </span>
            <span>
              Remaining:{" "}
              <span className="text-blue-600">{totals.totalRemaining}</span>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-gray-600 font-medium">
            <span className="flex items-center gap-1">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="h-3 w-3 text-gray-600"
              />
              {selectedShopName}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProductStockDetails;